"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CountFieldLogic = void 0;
const mean_1 = __importDefault(require("lodash-es/mean"));
const number_1 = require("../../aggregations/number");
const field_logic_1 = require("../../field-type-registry/field-logic");
const fields_logic_registry_1 = require("../../field-type-registry/fields-logic-registry");
const comparisons_1 = require("../../comparisons/comparisons");
const dom_1 = require("../../utils/dom");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const detailed_decimal_1 = require("../../formatters/detailed-decimal");
const intl_1 = require("../../locale/intl");
const count_fieldtype_1 = require("../../safe-field-value/calculated-fields/count-field/count.fieldtype");
const texts_1 = require("../../texts");
const linked_record_1 = require("../../utils/linked-record");
const math_1 = require("../../utils/math");
const base_number_fieldtype_1 = require("../base-fields/base-number.fieldtype");
const number_2 = require("../../utils/number");
class CountFieldLogic extends base_number_fieldtype_1.BaseNumberFieldLogic {
    constructor() {
        super(...arguments);
        this.name = "countfield";
        this.label = (0, intl_1.$t)(texts_1.countFieldText.count);
        this.icon = "count";
        this.iconColor = "#EC506E";
        this.placeholder = (0, intl_1.$t)(texts_1.countFieldText.enter_number_here);
        this.searchSynonyms = [(0, intl_1.$t)(texts_1.aggregatesText.sum).toLowerCase()];
        this.isCalculated = true;
        this.uneditableFieldError = (0, intl_1.$t)(texts_1.countFieldText.a_linked_record_field_is_required);
        // @ts-expect-error: Need to resolve.
        this.comparisons = {
            is_equal_to: comparisons_1.isEqualTo,
            is_not_equal_to: comparisons_1.isNotEqualTo,
            is_greater_than: comparisons_1.isGreaterThan,
            is_less_than: comparisons_1.isLessThanAndNotEmpty,
            is_equal_or_greater_than: comparisons_1.isEqualOrGreaterThan,
            is_equal_or_less_than: comparisons_1.isEqualOrLessThanAndNotEmpty,
            is_empty: comparisons_1.isObjectFieldEmpty,
            is_not_empty: comparisons_1.isNotObjectFieldEmpty,
        };
        this.aggregates = {
            empty: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.empty),
                aggregator: field_logic_1.countEmpty,
            },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            sum: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.sum),
                aggregator: (0, number_1.numbersAggregate)((values) => values.reduce((acc, value) => acc + value, 0)),
            },
            lowest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.lowest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.min(...values) : 0),
            },
            highest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.highest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.max(...values) : 0),
            },
            average: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.average),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, mean_1.default)(values) : 0),
            },
            median: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.median),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, math_1.median)(values) : 0),
            },
            std: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.standard_deviation),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, math_1.std)(values) : 0),
            },
            range: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.range),
                aggregator: number_1.nullableNumberRangeAggregator,
            },
        };
        this.fieldValueGetter = new count_fieldtype_1.CountFieldValueGetter();
        this.clipboardPasteRules = {};
    }
    isValueEmpty(fieldValue) {
        return fieldValue === null || fieldValue === undefined || fieldValue === 0;
    }
    valueToString(fieldValue, field) {
        if (this.isValueEmpty(fieldValue)) {
            return "0";
        }
        return [field.params.prefix, fieldValue, field.params.suffix]
            .filter(Boolean)
            .join("-");
    }
    getAggregateDisplayValue(fieldValue, _field, userPreferences) {
        var _a, _b;
        if (!fieldValue) {
            return "0";
        }
        return (0, detailed_decimal_1.convertNumberToFormatedString)({
            value: fieldValue,
            precision: 1,
            decimalSeparator: (0, number_2.getDecimalSeparator)((_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _a === void 0 ? void 0 : _a.value),
            thousandSeparator: (0, number_2.getGroupSeparator)((_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _b === void 0 ? void 0 : _b.value),
        });
    }
    valueToExport(fieldValue, field) {
        if (fieldValue === null || fieldValue === undefined) {
            return "0";
        }
        return [field.params.prefix, String(fieldValue), field.params.suffix]
            .filter(Boolean)
            .join(" ");
    }
    valueToGoogleSheet(fieldValue, field) {
        if (field.params.include_labels) {
            return (0, google_sheet_1.makeRowDataValues)(this.valueToExport(fieldValue, field), "stringValue");
        }
        const value = Number(fieldValue) || 0;
        return (0, google_sheet_1.makeRowDataValues)(value, "numberValue");
    }
    valueToExcelExport(fieldValue, field) {
        if (!isNaN(Number(fieldValue))) {
            return {
                t: "n",
                v: String(Number(fieldValue)),
            };
        }
        return {
            t: "t",
            v: this.valueToExport(fieldValue, field),
        };
    }
    getViewControlRules(field) {
        const mode = !!field.params.linked_field && !!field.params.target_application
            ? "show"
            : "disable";
        return {
            ...super.getViewControlRules(field),
            fieldInSort: mode,
            fieldInFilter: mode,
            fieldInGroup: mode,
            fieldInSpotlight: mode,
            disableFieldInTextSearch: mode,
        };
    }
    getEmptyValue() {
        return null;
    }
    hasUneditableFieldError(field, application) {
        return !(0, linked_record_1.hasLinkedFields)(application);
    }
    getCommonFieldParams(field) {
        return { ...super.getCommonFieldParams(field), system: false };
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            allowAddRecordToGroup: false,
            canDeleteValue: false,
        };
    }
    canChangeValue(field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue, field);
    }
    getInitialParams(field, structure) {
        const linkedField = (0, linked_record_1.getDefaultLinkedFieldSlug)(field, structure);
        return {
            linked_field: linkedField,
        };
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 120,
            minWidth: 60,
        };
    }
    canHaveReferencedRecords() {
        return true;
    }
    showSystemLockIcon() {
        return true;
    }
    /**
     * Get referenced records from both linked and target fields.
     */
    getReferencedRecords(fieldValue, field, record, structure) {
        var _a;
        if (!fieldValue) {
            return [];
        }
        const linkedField = (0, fields_1.getAppFieldBySlug)(structure, (_a = field.params.linked_field) !== null && _a !== void 0 ? _a : "");
        if (!linkedField) {
            return [];
        }
        const linkedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(linkedField.field_type);
        const linkedFieldValue = (0, fields_1.getValueFromRecordByField)(record, linkedField);
        return linkedFieldLogic.getReferencedRecords(linkedFieldValue, linkedField, record, structure);
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        const minPillWidth = 26;
        const pillPadding = 5;
        const strValue = this.valueToString(fieldValue, field);
        const size = (0, dom_1.getTextSize)(strValue, undefined, font);
        return Math.max(minPillWidth, size + pillPadding);
    }
    isSupportedByBulkAction() {
        return false;
    }
}
exports.CountFieldLogic = CountFieldLogic;
